import { Link } from 'react-router-dom';
import type { StandingsRowDTO } from '@pickem/shared';
import { useApi } from '../lib/useApi';
import { Spinner, ErrorBanner, EmptyState, SectionTitle } from '../components/ui';
import { ordinal } from '../lib/format';
import { useAuth } from '../lib/auth';

interface StandingsData {
  season: { id: string; year: number; name: string } | null;
  rows: StandingsRowDTO[];
}

export function StandingsPage() {
  const { data, error, loading } = useApi<StandingsData>('/standings');
  const { user } = useAuth();

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  if (!data || !data.season) {
    return <EmptyState title="No season yet">Standings will appear once the season begins.</EmptyState>;
  }
  if (data.rows.length === 0) {
    return (
      <EmptyState title="No results yet">
        Standings populate after the first week’s games are final.
      </EmptyState>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <SectionTitle>Season Standings</SectionTitle>
        <p className="-mt-2 text-sm text-muted">{data.season.name}</p>
        <Link to="/results" className="mt-1 inline-block text-sm font-medium text-grove hover:underline">
          Watch this week’s results live →
        </Link>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line bg-cream/60 text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-3 py-2">Rank</th>
              <th className="px-3 py-2">Player</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Bonus</th>
              <th className="px-3 py-2 text-right">Correct</th>
              <th className="px-3 py-2 text-right">Weeks</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const isMe = r.playerId === user?.id;
              return (
                <tr
                  key={r.playerId}
                  className={`border-b border-line last:border-0 ${
                    isMe ? 'bg-amber/10' : 'hover:bg-cream/50'
                  }`}
                >
                  <td className="px-3 py-3 font-display text-xl text-grove">{ordinal(r.rank)}</td>
                  <td className="px-3 py-3 font-medium">
                    {r.name}
                    {isMe && <span className="ml-2 chip bg-grove/10 text-grove">You</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-bold">{r.totalPoints}</td>
                  <td className="px-3 py-3 text-right text-muted">
                    {r.totalBonusPoints ? `+${r.totalBonusPoints}` : '—'}
                  </td>
                  <td className="px-3 py-3 text-right text-muted">{r.totalCorrectPicks}</td>
                  <td className="px-3 py-3 text-right text-muted">{r.weeksPlayed}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted">
        Ranked by total points (ranking points plus weekly bonuses). Correct picks shown as a
        secondary stat; genuine ties share a rank.
      </p>
    </div>
  );
}
